import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { catchError, retry, timer } from 'rxjs';
import { throwError } from 'rxjs';

/**
 * HTTP Error Interceptor
 * Handles HTTP errors globally with retry logic and user notifications
 */
export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const messageService = inject(MessageService);
  const translate = inject(TranslateService);

  return next(req).pipe(
    // Retry logic for transient errors (network issues, 5xx errors)
    retry({
      count: 2,
      delay: (error: HttpErrorResponse, retryCount) => {
        // Only retry on network errors or 5xx server errors
        if (error.status === 0 || error.status >= 500) {
          // Exponential backoff: 1s, 2s
          return timer(retryCount * 1000);
        }
        // Don't retry client errors (4xx)
        throw error;
      }
    }),
    // Catch and handle errors
    catchError((error: HttpErrorResponse) => {
      let errorMessage = '';

      if (error.error instanceof ErrorEvent) {
        // Client-side error
        errorMessage = translate.instant('errors.clientError', { message: error.error.message });
      } else {
        // Server-side error
        switch (error.status) {
          case 0:
            errorMessage = translate.instant('errors.networkError');
            break;
          case 400:
            errorMessage = translate.instant('errors.badRequest');
            break;
          case 401:
            errorMessage = translate.instant('errors.unauthorized');
            break;
          case 403:
            errorMessage = translate.instant('errors.forbidden');
            break;
          case 404:
            errorMessage = translate.instant('errors.notFound');
            break;
          case 500:
          case 502:
          case 503:
          case 504:
            errorMessage = translate.instant('errors.serverError');
            break;
          default:
            errorMessage = translate.instant('errors.unknown', { status: error.status });
        }
      }

      // Show error notification (except for 401 - handled by auth)
      if (error.status !== 401) {
        messageService.add({
          severity: 'error',
          summary: translate.instant('errors.title'),
          detail: errorMessage,
          life: 5000
        });
      }

      // Re-throw the error so it can be handled by the calling code if needed
      return throwError(() => error);
    })
  );
};
